import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  TrendingUp, Loader2, CheckCircle, Zap, Target, Rocket, Crown
} from "lucide-react";

const TIERS = [
  { 
    id: "basic", 
    name: "Basic", 
    description: "Project-based consulting",
    icon: Target,
    color: "bg-neutral-100 text-neutral-700"
  },
  { 
    id: "premium_rapid", 
    name: "R.A.P.I.D.™ Business Recalibration", 
    description: "5-session leadership transformation",
    icon: Zap,
    color: "bg-purple-100 text-purple-700"
  },
  { 
    id: "growth_strategic", 
    name: "Strategic Foundation", 
    description: "Business strategy development",
    icon: Target,
    color: "bg-blue-100 text-blue-700"
  },
  { 
    id: "growth_optimization", 
    name: "Growth & Optimization", 
    description: "Scaling and process improvement",
    icon: Rocket,
    color: "bg-emerald-100 text-emerald-700"
  },
  { 
    id: "growth_scale", 
    name: "Scale & Leadership", 
    description: "Enterprise growth and leadership",
    icon: Crown,
    color: "bg-amber-100 text-amber-700"
  }
];

export const UpgradeRequestDialog = ({ open, onOpenChange, currentTier }) => {
  const [selectedTier, setSelectedTier] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const availableTiers = TIERS.filter(t => t.id !== currentTier);

  const handleSubmit = async () => {
    if (!selectedTier) {
      toast.error("Please select a plan");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/client/request-upgrade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          tier: selectedTier,
          message: message.trim()
        })
      });

      if (response.ok) {
        toast.success("Upgrade request submitted! Your consultant will be notified.");
        onOpenChange(false);
        setSelectedTier("");
        setMessage("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit request");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-[#39FF14]" />
            Request Plan Upgrade
          </DialogTitle>
          <DialogDescription>
            Select the plan you're interested in. Your consultant will review your request.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Plan */}
          {currentTier && (
            <div className="p-3 bg-neutral-50 rounded-lg">
              <p className="text-sm text-neutral-500">Current Plan</p>
              <p className="font-medium text-neutral-900">
                {TIERS.find(t => t.id === currentTier)?.name || "Basic"}
              </p>
            </div>
          )}

          {/* Plan Selection */}
          <div className="space-y-2">
            <Label>Select New Plan</Label>
            <RadioGroup value={selectedTier} onValueChange={setSelectedTier}>
              <div className="space-y-2">
                {availableTiers.map((tier) => {
                  const Icon = tier.icon;
                  return (
                    <div
                      key={tier.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTier === tier.id 
                          ? "border-[#39FF14] bg-[#39FF14]/5" 
                          : "border-neutral-200 hover:border-neutral-300"
                      }`}
                      onClick={() => setSelectedTier(tier.id)}
                    >
                      <RadioGroupItem value={tier.id} id={tier.id} />
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tier.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-neutral-900">{tier.name}</p>
                        <p className="text-sm text-neutral-500">{tier.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </RadioGroup>
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message to Consultant (Optional)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Share why you're interested in upgrading..."
              className="min-h-20"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !selectedTier}
            className="bg-[#333333] hover:bg-[#444444] text-white"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-2" />
            )}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeRequestDialog;
